import type { TalentOfferStatus as TalentOfferStatusType } from "@js/types/jobs";
import { assertUnreachable } from "@js/utils";

import { OfferStatusChip } from "./offer-status-chip";

type EmployerOfferStatusProps = {
  offerStatus: EnumType<typeof ENUMS.OfferStatus> | null;
};

export const EmployerOfferStatus = ({
  offerStatus,
}: EmployerOfferStatusProps) => {
  const { color, label } = getEmployerOfferStatus(offerStatus);

  return <OfferStatusChip label={label} color={color} />;
};

type TalentOfferStatusProps = {
  offerStatus: TalentOfferStatusType;
};

export const TalentOfferStatus = ({ offerStatus }: TalentOfferStatusProps) => {
  const { color, label } = getTalentOfferStatus(offerStatus);

  return <OfferStatusChip label={label} color={color} />;
};

const getEmployerOfferStatus = (
  status: EnumType<typeof ENUMS.OfferStatus> | null,
) => {
  switch (status) {
    case null:
      return {
        color: "green",
        label: "Creating offer",
      } as const;

    case ENUMS.OfferStatus.ACCEPTED:
      return {
        color: "green",
        label: "Offer accepted 🎉",
      } as const;

    case ENUMS.OfferStatus.REJECTED:
      return {
        color: "red",
        label: "Offer declined",
      } as const;

    case ENUMS.OfferStatus.CANCELLED:
      return {
        color: "red",
        label: "Canceled by Client",
      } as const;

    case ENUMS.OfferStatus.EXPIRED:
      return {
        color: "grey",
        label: "Offer expired",
      } as const;

    case ENUMS.OfferStatus.NEW:
    case ENUMS.OfferStatus.UPDATED:
      return {
        color: "blue",
        label: "Pending offer",
      } as const;

    case ENUMS.OfferStatus.WAITING_FOR_DEPOSIT:
    case ENUMS.OfferStatus.DEPOSIT_PAYMENT_IN_PROGRESS:
    case ENUMS.OfferStatus.WAITING_FOR_DEPOSIT_ACH_CONFIRMATION:
      return {
        color: "blue",
        label: "Pending billing confirmation",
      } as const;

    case ENUMS.OfferStatus.DEPOSIT_PAYMENT_FAILED:
      return {
        color: "red",
        label: "Payment method failed",
      } as const;

    case ENUMS.OfferStatus.EXTENDED:
      return {
        color: "green",
        label: "Contract updated",
      } as const;

    default:
      assertUnreachable(status);
      return {
        color: "blue",
        label: "Pending offer",
      } as const;
  }
};

export const getTalentOfferStatus = (status: TalentOfferStatusType | null) => {
  switch (status) {
    case null:
    case ENUMS.OfferStatus.ACCEPTED:
      return {
        color: "green",
        label: "Offer accepted 🎉",
      } as const;

    case ENUMS.OfferStatus.REJECTED:
      return {
        color: "red",
        label: "Offer declined",
      } as const;

    case ENUMS.OfferStatus.CANCELLED:
      return {
        color: "red",
        label: "Canceled by Client",
      } as const;

    case ENUMS.OfferStatus.EXPIRED:
      return {
        color: "grey",
        label: "Offer expired",
      } as const;

    case ENUMS.OfferStatus.NEW:
      return {
        color: "blue",
        label: "New offer",
      } as const;

    case ENUMS.OfferStatus.UPDATED:
      return {
        color: "blue",
        label: "Updated offer",
      } as const;

    case ENUMS.OfferStatus.EXTENDED:
      return {
        color: "green",
        label: "Contract updated",
      } as const;

    default:
      assertUnreachable(status);
      return {
        color: "blue",
        label: "New offer",
      } as const;
  }
};
